import { useHasFeature } from '@/design-system/components/hasFeature/useHasFeature.hook';
import { popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H5 } from '@/design-system/components/typography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { Pressable, ScrollView, SxProp, View } from 'dripsy';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { RootStackRoutes } from 'types';
import { PAYMENT_TYPE } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { BillPaymentLandingItem } from '../components/BillPaymentLandingItem';
import { HelpCenterComponent } from '../components/HelpCenter.component';

export interface BillPaymentLandingWebProps {}
export interface LandingItem {
  name: string;
  iconName: string;
  title: string;
  description: string;
  parentScreenName?: string;
  childScreenName?: string;
  params?: { [key: string]: string };
  showFlag?: boolean;
}

export const BillPaymentLandingWebComponent: React.FC<BillPaymentLandingWebProps> = () => {
  const { t } = useTranslation('global-bill-payment');
  const navigation = useNavigation<any>();
  const NUM_COLUMN = 2;

  useFocusEffect(
    useCallback(() => {
      const onPopState = (event: any) => {
        window.history.pushState(event.state, '');
        setTimeout(() => {
          const popStateLocked = popStateLockedMap.get('popstate') ?? false;
          if (!document.location.href || popStateLocked) return;
          iframePostMessage('backToOverview', true);
        }, 200);
      };

      if (Platform.OS === 'web') {
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  const createGroup = (data: LandingItem[], numColumns: number) => {
    const dataGroup: LandingItem[][] = [];
    let groupCount = 0;
    let group: LandingItem[] = [];
    data.map((item: LandingItem, index: number) => {
      if (!item?.showFlag) return;
      if (groupCount === 0 && index !== 0) {
        dataGroup.push(group);
        group = [];
      }
      group.push(item);
      groupCount++;
      if (groupCount === numColumns) groupCount = 0;
      if (data.length - 1 === index) dataGroup.push(group);
    });
    return dataGroup;
  };

  const payments: LandingItem[] = [
    {
      name: 'Local Bill Payment',
      iconName: 'oversealocalbp',
      title: t('glossary.title--local-bill-payment'),
      description: t('global-bill-payment::landing.text--local-bill-payment'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.Instruction,
      params: {
        paymentType: PAYMENT_TYPE.LOCAL
      },
      showFlag: useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled
    },
    {
      name: 'Oversea Bill Payment',
      iconName: 'overseabp',
      title: t('global-bill-payment::glossary.title--overseas-bill-payment'),
      description: t('global-bill-payment::landing.text--overseas-bill-payment'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.Instruction,
      params: {
        paymentType: PAYMENT_TYPE.GLOBAL
      },
      showFlag: useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled
    },
    {
      name: 'Maintenance',
      iconName: 'maintenancebp',
      title: t('global-bill-payment::landing.title--maintenance'),
      description: t('global-bill-payment::landing.text--bp-maintenance'),
      showFlag:
        useHasFeature({ feature: 'DIGITAL_WITHDRAW', variation: 'enabled' })?.enabled ||
        useHasFeature({ feature: 'FIAT_WITHDRAW', variation: 'enabled' })?.enabled
    },
    {
      name: 'UnionPay',
      iconName: 'unionPayBP',
      title: t('global-bill-payment::landing.title--union-pay'),
      description: t('global-bill-payment::landing.text--bp-union-pay'),
      parentScreenName: RootStackRoutes.BillPayment,
      childScreenName: BillPaymentStackRoutes.UnionPay,
      showFlag: useHasFeature({ feature: 'UNIONPAY_REMITTANCE', variation: 'beta' })?.enabled
    }
    // {
    //   name: 'Acquisition',
    //   iconName: 'acquisitionbp',
    //   title: t('global-bill-payment::general.acquisition'),
    //   description: t('global-bill-payment::landing.text--bp-acquisition')
    // },
    // {
    //   name: 'Investment',
    //   iconName: 'investmentbp',
    //   title: t('global-bill-payment::landing.title--investment'),
    //   description: t('global-bill-payment::landing.text--bp-investment')
    // }
  ];

  const transfers: LandingItem[] = [
    {
      name: 'General Transfer',
      iconName: 'transferbp',
      title: t('global-bill-payment::landing.title--general-transfer'),
      description: t('global-bill-payment::landing.text--general-transfer'),
      showFlag: useHasFeature({ feature: 'TRANSFER', variation: 'enabled' })?.enabled
    },
    {
      name: 'Exchange Transfer',
      iconName: 'exchangebp',
      title: t('global-bill-payment::landing.title--bp-transfer'),
      description: t('global-bill-payment::landing.text--bp-transfer'),
      showFlag: useHasFeature({ feature: 'EXCHANGE_SUB_ACCOUNT', variation: 'enabled' })?.enabled
    }
  ];

  const onNavigate = (item: LandingItem) => {
    if (item.parentScreenName && item.childScreenName) {
      navigation.navigate(item.parentScreenName, {
        screen: item.childScreenName,
        params: item.params
      });
    }
    switch (item.name) {
      case 'Maintenance':
        return iframePostMessage('showMaintenance', true);
      case 'General Transfer':
        return iframePostMessage('showTransfer', true);
      default:
        return null;
    }
  };

  return (
    <View sx={sxStyles.container}>
      <ScrollView sx={sxStyles.scrollView} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.leftComponent}>
            {(useHasFeature({ feature: 'BILL_PAYMENT', variation: 'beta' })?.enabled ||
              useHasFeature({ feature: 'UNIONPAY_REMITTANCE', variation: 'enabled' })?.enabled) && (
              <H5 bold sx={sxStyles.textPayment}>
                {t('global-bill-payment::glossary.title--payment')}
              </H5>
            )}
            {createGroup(payments, NUM_COLUMN).map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <View sx={sxStyles.rowView} key={index}>
                    {item.map((item2: LandingItem, index2: React.Key | null | undefined) => {
                      if (item2?.showFlag == false) return null;
                      return (
                        <React.Fragment key={index2}>
                          <View sx={sxStyles.viewItemWrapper}>
                            <Pressable onPress={() => onNavigate(item2)}>
                              <BillPaymentLandingItem title={item2.title} description={item2.description} iconName={item2.iconName} />
                            </Pressable>
                          </View>
                          {(item.length - 1 !== index2 || item.length < NUM_COLUMN) && <View sx={sxStyles.spaceHorizontal} />}
                          {item.length < NUM_COLUMN && <View sx={sxStyles.viewItemWrapper} />}
                        </React.Fragment>
                      );
                    })}
                  </View>
                  <View sx={sxStyles.spaceVertical} />
                </React.Fragment>
              );
            })}
            {(useHasFeature({ feature: 'TRANSFER', variation: 'enabled' })?.enabled ||
              useHasFeature({ feature: 'EXCHANGE_SUB_ACCOUNT', variation: 'enabled' })?.enabled) && (
              <>
                <View sx={sxStyles.space40Vertical} />
                <H5 bold sx={sxStyles.textPayment}>
                  {t('global-bill-payment::glossary.title--transfer')}
                </H5>
              </>
            )}
            {createGroup(transfers, NUM_COLUMN).map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <View sx={sxStyles.rowView} key={`transfer-${index}`}>
                    {item.map((item2, index2) => {
                      if (item2?.showFlag == false) return null;
                      return (
                        <React.Fragment key={`transfer-${index2}`}>
                          <View sx={sxStyles.viewItemWrapper}>
                            <Pressable onPress={() => onNavigate(item2)}>
                              <BillPaymentLandingItem title={item2.title} description={item2.description} iconName={item2.iconName} />
                            </Pressable>
                          </View>
                          {(item.length - 1 !== index2 || item.length < NUM_COLUMN) && <View sx={sxStyles.spaceHorizontal} />}
                          {item.length < NUM_COLUMN && <View sx={sxStyles.viewItemWrapper} />}
                        </React.Fragment>
                      );
                    })}
                  </View>
                  <View sx={sxStyles.spaceVertical} />
                </React.Fragment>
              );
            })}
          </View>
          <View sx={sxStyles.space40} />
          <View sx={sxStyles.rightComponent}>
            <HelpCenterComponent />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flex: 1
  },
  scrollView: {
    padding: '$3'
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10,
    overflow: 'hidden',
    borderRadius: '$radius3',
    flexDirection: 'row'
  },
  leftComponent: {
    flex: 1160,
    pl: '$10',
    pt: '$10',
    pb: '$10'
  },
  rightComponent: {
    flex: 380,
    pt: '$10',
    pr: '$10'
  },
  rowView: {
    flexDirection: 'row',
    width: '100%'
  },
  viewItemWrapper: {
    flex: 1
  },
  spaceHorizontal: {
    width: '$5'
  },
  spaceVertical: {
    height: '$5'
  },
  space40: {
    width: '$10'
  },
  space40Vertical: {
    height: '$10'
  },
  textPayment: {
    mb: '$5'
  }
};
